import { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Password input with a built-in show/hide eye toggle.
 *
 * - Default state: hidden (type="password").
 * - Click the eye icon to flip to type="text" and back.
 * - Icon swaps between <Eye/> (currently hidden, click to show) and
 *   <EyeOff/> (currently visible, click to hide) so the icon reflects
 *   what the *next* click will do.
 * - Forwards all standard Input props except `type`.
 *
 * Usage:
 *   <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} data-testid="x" />
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testid, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        data-testid={testid}
        // Reserve room for the toggle so long passwords don't slide under it.
        className={`pr-11 ${className}`}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        data-testid={testid ? `${testid}-toggle` : "password-toggle"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-flag-navy focus:outline-none focus-visible:text-flag-navy"
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
});

export default PasswordInput;
